import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import "./styles.scss";

import {
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconInfinity,
  IconQrcode,
  IconRefresh,
  IconRepeat,
} from "@tabler/icons-react";

import { Modal, Text } from "@fragments";
import { CommunityBanner } from "@components/Community";

import Parser from "@helpers/parser";

export default function SharePlanModal({ open, onClose, plan, community }) {
  const { t } = useTranslation("settings");
  const [copied, setCopied] = useState(false);

  const REF_copiedTimeout = useRef();

  const link = plan
    ? `https://pay.ginastee.com/${community?.checkout}?plan=${plan.id}`
    : "";

  useEffect(() => {
    setCopied(false);
    return () => clearTimeout(REF_copiedTimeout.current);
  }, [open, plan?.id]);

  function handleCopy() {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setCopied(true);
        clearTimeout(REF_copiedTimeout.current);
        REF_copiedTimeout.current = setTimeout(() => {
          setCopied(false)
          window.setSnackbar(t("shared.linkCopied"))
          onClose?.()
        }, 700);
      })
      .catch((err) => {
        console.error("Erro ao copiar:", err);
        window.setSnackbar("Erro ao copiar o link");
      });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={350}
      title={t("pricing.share.title")}
      subtitle={t("pricing.share.subtitle")}
      className="share-plan-modal pb-2"
    >
      <div className="spm-hero">
        <div className="spm-hero-community">
          <CommunityBanner size={44} banner={community?.banner} />
          <div className="spm-hero-desc">
            <Text.subheading contrast={6}>{t("common:entities.community")}</Text.subheading>
            <Text.h6 contrast={9} weight={500} className="spm-hero-name">
              {community?.name}
            </Text.h6>
          </div>
        </div>

        <div className="spm-hero-divider" />

        <div className="spm-hero-plan">
          <Text.subheading contrast={6}>{plan?.label}</Text.subheading>

          <div className="spm-hero-price">
            <Text.h3 contrast={9} weight={500}>
              R${Parser.price.brl(plan?.price)}
            </Text.h3>
            <Text.span contrast={6}>
              · {Parser.interval.adjective(plan?.interval)}
            </Text.span>
          </div>

          {plan?.installments > 1 &&
            plan?.installments_list?.[plan.installments - 1] && (
              <Text.caption contrast={5}>
                {t("pricing.share.installments", {
                  installments: plan.installments,
                  amount: Parser.price.brl(
                    plan.installments_list[plan.installments - 1] /
                      plan.installments,
                  ),
                })}
              </Text.caption>
            )}

          <div className="spm-hero-chips">
            <div className="spm-chip">
              {plan?.interval === "lifetime" ? (
                <IconInfinity size={14} />
              ) : !plan?.autorenew ? (
                <IconRefresh size={14} />
              ) : (
                <IconRepeat size={14} />
              )}
              <Text.caption contrast={6}>
                {plan?.interval === "lifetime"
                  ? t("pricing.share.lifetime")
                  : plan?.autorenew
                    ? t("pricing.share.autorenew")
                    : t("pricing.share.oneTime")}
              </Text.caption>
            </div>
            {plan?.methods?.credit?.enabled && (
              <div className="spm-chip">
                <IconCreditCard size={14} />
                <Text.caption contrast={6}>{t("pricing.methods.credit")}</Text.caption>
              </div>
            )}
            {plan?.methods?.pix?.enabled && (
              <div className="spm-chip">
                <IconQrcode size={14} />
                <Text.caption contrast={6}>{t("pricing.methods.pix")}</Text.caption>
              </div>
            )}
          </div>
        </div>
      </div>

      <Text.h5 className="mt-2" contrast={8} weight={400}>
        {t("pricing.share.copyHint")}
      </Text.h5>

      <div
        className={`mt-1 spm-link${copied ? " is-copied" : ""}`}
        role="button"
        tabIndex={0}
        onClick={handleCopy}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCopy();
          }
        }}
        title={copied ? t("shared.linkCopied") : t("pricing.share.copyTooltip")}
      >
        <Text.span contrast={7} className="spm-link-url">
          {link}
        </Text.span>
        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
      </div>
    </Modal>
  );
}
